import { ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/files/FileFields";
import { age } from "@/lib/clinic";
import { fmtDate } from "@/lib/format";

const Box = ({ children, className = "", testid }) => <div className={`border border-[#9A9A9A] rounded-sm p-3 leading-5 ${className}`} data-testid={testid}>{children}</div>;

// Shared A4 document header (same style as Factura / Arqueo): logo, issuer block, title box, patient box
export function ReportHeader({ title, patient, date, code }) {
  const { view, t, lang } = useApp();
  const s = view.shop || {};
  return (
    <div className="text-[12px] text-[#222] mb-4" data-testid="report-header">
      {s.logo_id && <div className="mb-3"><FileImage id={s.logo_id} alt={s.name} className="h-14 max-w-[180px] object-contain object-left" /></div>}
      <div className="grid grid-cols-[1.1fr_0.9fr_1.2fr] gap-4 items-start">
        <div className="leading-5" data-testid="report-issuer">
          <div className="font-extrabold text-sm uppercase">{s.name}</div>
          {s.tax_id && <div dir="ltr">{s.tax_id}</div>}
          {s.address && <div>{s.address}</div>}
          {s.phone && <div dir="ltr">{s.phone}</div>}
        </div>
        <Box className="text-center" testid="report-title-box">
          <div className="font-extrabold text-base tracking-wide uppercase">{title}</div>
          {code && <div className="font-mono font-bold mt-1 text-[#C0392B]" dir="ltr" data-testid="report-rx-code">℞ {code}</div>}
          {date && <div dir="ltr">{fmtDate(date, lang, false)}</div>}
        </Box>
        <Box className="min-h-[88px]" testid="report-patient-box">
          {patient ? <>
            <div className="font-bold uppercase">{patient.name}</div>
            {patient.file_no && <div>{t("file_no")}: <span dir="ltr" data-testid="report-file-no">{patient.file_no}</span></div>}
            <div>{[patient.birth_date && `${age(patient.birth_date)} ${t("years_short")}`, patient.gender && t(`gender_${patient.gender}`)].filter(Boolean).join(" · ")}{patient.phone && <span> · <span dir="ltr">{patient.phone}</span></span>}</div>
            {patient.address && <div>{patient.address}</div>}
          </> : <div className="text-[#777]">{t("patient")}</div>}
        </Box>
      </div>
    </div>
  );
}

export const RTh = ({ children, end, w }) => <th style={{ width: w }} className={`py-1 px-2 font-semibold text-[11px] text-[#333] bg-[#E3E3E3] border-b border-[#BDBDBD] whitespace-nowrap ${end ? "text-end" : "text-start"}`}>{children}</th>;
export const RTd = ({ children, end, bold, dir, nowrap }) => <td dir={dir} className={`py-1 px-2 align-top border-b border-[#EDEDED] ${nowrap ? "whitespace-nowrap" : ""} ${end ? "text-end" : "text-start"} ${bold ? "font-bold" : ""}`}>{children}</td>;
export const RSection = ({ title, children }) => <div className="mt-4 pdf-block"><div className="font-bold text-[12px] mb-1">{title}</div>{children}</div>;

export function ReportFooter({ doctorName, doctorId }) {
  const { view, t } = useApp();
  const s = view.shop || {};
  const doc = view.staff.find((x) => x.id === doctorId) || view.staff.find((x) => x.name === doctorName);
  return (
    <div className="mt-8 pdf-block pdf-foot text-[12px] text-[#222]" data-testid="report-footer">
      <div className="flex items-end justify-end gap-6">
        {doc?.stamp_id && <div className="h-36 w-36 grid place-items-center" data-testid="report-stamp"><FileImage id={doc.stamp_id} alt="" className="max-h-36 max-w-36 object-contain" /></div>}
        <div className="text-center w-64">
          {doc?.signature_id ? <div className="h-16 flex items-end justify-center" data-testid="report-signature"><FileImage id={doc.signature_id} alt="" className="max-h-16 object-contain" /></div> : <div className="h-16" />}
          <div className="border-t border-[#333] pt-1 font-bold">{doctorName || doc?.name}</div>
          <div className="text-[#555]">{t("doctor")}{doc?.specialty && ` · ${t(`sp_${doc.specialty}`)}`}</div>
        </div>
      </div>
      <div className="mt-6 border-t border-[#DDD] pt-2 text-[8.5px] leading-[11px] text-[#555] text-center" data-testid="report-legal">
        <div className="font-semibold">{[s.name, s.address, s.tax_id, s.phone].filter(Boolean).join(" · ")}</div>
        {s.doc_footer && <div className="whitespace-pre-line text-justify mt-0.5">{s.doc_footer}</div>}
      </div>
    </div>
  );
}

// Deterministic verification code (VSC) derived from the Rx code, patient, doctor and exact issue instant
export function vscCode(code, issuedAt, patientId, doctorId) {
  const src = `${code}|${issuedAt}|${patientId || ""}|${doctorId || ""}`;
  let h1 = 0x811c9dc5, h2 = 0x9747b28c;
  for (let i = 0; i < src.length; i++) { const c = src.charCodeAt(i); h1 = Math.imul(h1 ^ c, 16777619) >>> 0; h2 = Math.imul(h2 ^ c, 2246822519) >>> 0; }
  const hex = (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0")).toUpperCase();
  return `VSC-${hex.slice(0, 4)}-${hex.slice(4, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}`;
}

const pad = (n) => String(n).padStart(2, "0");
export const fmtIssued = (iso) => { const d = new Date(iso); return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; };

export function RxValidation({ code, issuedAt, patientId, doctorId }) {
  const { t } = useApp();
  const vsc = vscCode(code, issuedAt, patientId, doctorId);
  return (
    <div className="mt-4 border border-[#9A9A9A] rounded-sm p-3 flex items-center gap-3 pdf-block" data-testid="rx-validation">
      <div className="w-11 h-11 rounded-full bg-[#333] text-white grid place-items-center shrink-0"><ShieldCheck className="w-6 h-6" /></div>
      <div className="flex-1 min-w-0 text-[11px] text-[#555]">
        <div className="flex flex-wrap items-baseline gap-x-3"><span className="uppercase tracking-widest text-[10px] font-bold text-[#222]">{t("vsc_title")}</span><b className="font-mono text-sm text-[#222] tracking-wider" dir="ltr" data-testid="rx-vsc">{vsc}</b></div>
        <div className="mt-0.5"><span>{t("vsc_issued")}:</span> <b className="font-mono text-[#12384A]" dir="ltr" data-testid="rx-issued-at">{fmtIssued(issuedAt)}</b>{code && <span className="ms-3">℞ <b className="font-mono" dir="ltr">{code}</b></span>}</div>
        <div className="mt-0.5">{t("vsc_hint")}</div>
      </div>
    </div>
  );
}

export function RxSheet({ patient, date, code, issuedAt, diagnosis, rows, doctorName, doctorId, notes }) {
  const { view, t } = useApp();
  const doc = view.staff.find((x) => x.id === doctorId) || view.staff.find((x) => x.name === doctorName);
  return (
    <div className="bg-white text-[#222] text-[12px] p-6 pdf-page flex flex-col" data-testid="rx-sheet">
      <ReportHeader title={t("prescription")} patient={patient} date={date} code={code} />
      {diagnosis && <div className="mb-2"><b>{t("diagnosis")}:</b> {diagnosis}</div>}
      <RSection title={t("prescription")}>
        <table className="w-full">
          <thead><tr><RTh w="6%">Nº</RTh><RTh>{t("drug")}</RTh><RTh w="18%">{t("dose")}</RTh><RTh w="16%">{t("duration")}</RTh><RTh w="24%">{t("hr_notes")}</RTh></tr></thead>
          <tbody>
            {(rows || []).map((x, i) => <tr key={x.id || i} data-testid={`rx-line-${i}`}><RTd dir="ltr" bold>{i + 1}</RTd><RTd bold>{x.drug}</RTd><RTd dir="ltr">{x.dose}</RTd><RTd>{x.duration}</RTd><RTd>{x.notes}</RTd></tr>)}
            {!rows?.length && <tr><td colSpan={5} className="py-1 px-2">—</td></tr>}
          </tbody>
        </table>
      </RSection>
      {notes && <p className="mt-3 whitespace-pre-line">{notes}</p>}
      <div className="pdf-foot mt-auto">
        <ReportFooter doctorName={doctorName} doctorId={doctorId} />
        <RxValidation code={code} issuedAt={issuedAt || `${date}T00:00:00`} patientId={patient?.id} doctorId={doc?.id || doctorId} />
        <p className="text-[9px] text-[#555] mt-2">{t("erx_footer")}</p>
      </div>
    </div>
  );
}
